import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, Download, Loader2 } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import formDocumentIcon from "@/shared/assets/probate/form-document.svg";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAuthorityContext } from "@/shared/hooks/useAuthorityContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import {
  getRequiredForms,
  PROBATE_FORM_NAMES,
  PROBATE_FORM_URLS,
} from "@/shared/constants/probateForms";
import { Button } from "@/shared/components/ui/button";
import { trackEvent, EventType, cn } from "@/shared/utils/core";
import { parseConsentEntries } from "@/shared/utils/consentUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { ProbateAnswers, HeirConsentOption, WitnessStatus } from "@/shared/types/probateTypes";
import type { ProbateFormId } from "@/shared/constants/probateForms";

function parseConsents(raw: string | string[] | undefined): Record<string, HeirConsentOption> {
  const entries = parseConsentEntries(raw);
  const result: Record<string, HeirConsentOption> = {};
  for (const [id, entry] of Object.entries(entries)) {
    result[id] = entry.status;
  }
  return result;
}

function buildProbateAnswers(answers: Record<string, string | string[]>): ProbateAnswers {
  const out: ProbateAnswers = {};

  const willLocation = answers["will_location"];
  if (willLocation === "original_in_hand" || willLocation === "copy_only" || willLocation === "safe_deposit_box") {
    out.will_location = willLocation;
  }

  const executorStatus = answers["executor_status"];
  const successorExecutor = answers["successor_executor"];
  if (executorStatus === "unable" && successorExecutor === "successor_willing") {
    out.executor_status = "successor_willing";
  } else if (
    executorStatus === "able_and_willing" ||
    executorStatus === "unable" ||
    executorStatus === "successor_willing" ||
    executorStatus === "all_decline"
  ) {
    out.executor_status = executorStatus;
  }

  const selfProving = answers["self_proving_affidavit"];
  if (typeof selfProving === "string") {
    out.self_proving_affidavit = selfProving === "yes" || selfProving === "true";
  }

  const rawWitnessStatus = answers["witness_status"];
  if (typeof rawWitnessStatus === "string" && rawWitnessStatus) {
    try {
      const parsed: unknown = JSON.parse(rawWitnessStatus);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        out.witness_status = parsed as Record<string, WitnessStatus>;
      } else if (typeof parsed === "string") {
        out.witness_status = { "0": parsed as WitnessStatus };
      }
    } catch {
      out.witness_status = { "0": rawWitnessStatus as WitnessStatus };
    }
  }

  const rawConsents = answers["family_signoff_consents"];
  const consents = parseConsents(rawConsents);
  if (Object.keys(consents).length > 0) {
    out.heir_consents = consents;
  }

  return out;
}


interface ExpandedFormRow {
  formId: ProbateFormId;
  signerName: string | null;
  isHeir: boolean;
  key: string;
}

interface FormRowProps {
  formId: ProbateFormId;
  signerLabel: string;
  signerIsHeir: boolean;
}

const FormRow: FC<FormRowProps> = ({ formId, signerLabel, signerIsHeir }) => (
  <div className="flex items-center gap-3 sm:gap-4 bg-white p-3 sm:p-4 border-b border-primary last:border-b-0">
    <img src={formDocumentIcon} alt="" className="size-5 sm:size-8 shrink-0" aria-hidden="true" />
    <div className="flex flex-1 flex-col min-w-0 justify-center">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        <span className="text-sm font-medium leading-[1.55]">{PROBATE_FORM_NAMES[formId]}</span>
        <span className="border border-primary rounded-sm px-1 py-0.5 font-mono text-xs text-secondary shrink-0 leading-[1.55]">
          Form {formId}
        </span>
      </div>
      <span className={cn("text-[13px] leading-[1.55]", signerIsHeir ? "font-medium text-[#1e90d9]" : "font-normal text-secondary")}>
        {signerLabel}
      </span>
    </div>
    <Button
      variant="outline"
      className="shrink-0 gap-2"
      aria-label={`Download ${formId} PDF`}
      onClick={() => window.open(PROBATE_FORM_URLS[formId], "_blank")}
    >
      <Download className="size-4" aria-hidden="true" />
      PDF
    </Button>
  </div>
);

export const ProbateResultsScreen: FC<FlowCustomComponentProps> = ({ answers }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { county } = useAuthorityContext(estateId);
  const { contacts } = useUserContacts();

  const probateAnswers = useMemo(() => buildProbateAnswers(answers), [answers]);
  const requiredForms = useMemo(() => getRequiredForms(probateAnswers), [probateAnswers]);

  const contactsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of contacts) {
      const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
      if (fullName) map.set(String(contact.id), fullName);
    }
    return map;
  }, [contacts]);

  const expandedForms = useMemo((): ExpandedFormRow[] => {
    const result: ExpandedFormRow[] = [];
    for (const formId of requiredForms) {
      if (formId === "P-4" && probateAnswers.heir_consents) {
        for (const [contactId, status] of Object.entries(probateAnswers.heir_consents)) {
          if (status === "willing_to_sign") {
            result.push({
              formId,
              signerName: contactsById.get(contactId) ?? null,
              isHeir: true,
              key: `${formId}-${contactId}`,
            });
          }
        }
      } else {
        result.push({ formId, signerName: null, isHeir: false, key: formId });
      }
    }
    return result;
  }, [requiredForms, probateAnswers.heir_consents, contactsById]);

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent(EventType.PROBATE_FORMS_GENERATED, {
      estate_id: estateId,
      form_count: requiredForms.length,
      form_ids: requiredForms,
    });
  }, []);

  const decedentName = person?.first_name ?? "the decedent";
  const countyName = county ?? "New York";


  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPacket = useCallback(async () => {
    if (isDownloading || requiredForms.length === 0) return;
    setIsDownloading(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const fetches = requiredForms.map(async (formId) => {
        const url = PROBATE_FORM_URLS[formId];
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed to fetch ${formId}`);
        const blob = await res.blob();
        zip.file(`${formId} ${PROBATE_FORM_NAMES[formId]}.pdf`, blob);
      });
      await Promise.all(fetches);
      const content = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(content);
      link.download = `${decedentName}-probate-forms.zip`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) {
      console.error("Failed to generate zip:", err);
      enqueueSnackbar("Couldn't download forms. Please try again.", { variant: "error" });
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, requiredForms, decedentName]);

  return (
    <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex flex-1 flex-col gap-4 min-w-0">
            <span className="inline-flex self-start items-center gap-1 rounded-full border border-green-300 bg-green-100 px-2.5 py-0.5">
              <Check className="size-3 text-brand" aria-hidden="true" />
              <span className="text-sm font-medium text-brand">Your results</span>
            </span>
            <div className="flex flex-col gap-2">
              <p className="font-sans text-xl font-semibold leading-[30px]">
                {decedentName}'s probate forms are ready
              </p>
              <p className="text-sm text-secondary">
                Based on your answers, we prepared the {requiredForms.length}{" "}
                {requiredForms.length === 1 ? "form" : "forms"} Surrogate's Court in {countyName} needs.
                Review each one, then sign where shown.
              </p>
            </div>
          </div>
          <Button
            variant="brand"
            className="shrink-0 gap-2"
            disabled={isDownloading}
            onClick={handleDownloadPacket}
          >
            {isDownloading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Preparing...
              </>
            ) : (
              <>
                Download packet
                <ArrowRight className="size-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </div>

        <div className="rounded-lg border border-primary overflow-hidden">
          <div className="bg-white p-4 border-b border-primary">
            <span className="text-sm font-medium leading-[1.55]">
              Your packet &middot; {expandedForms.length} {expandedForms.length === 1 ? "form" : "forms"}
            </span>
          </div>
          {expandedForms.map(({ formId, signerName, isHeir, key }) => {
            const signerLabel = isHeir && signerName ? `${signerName} signs` : "You sign";
            return (
              <FormRow
                key={key}
                formId={formId}
                signerLabel={signerLabel}
                signerIsHeir={isHeir}
              />
            );
          })}
        </div>

        <div className="rounded-lg border border-primary bg-white p-6 flex flex-col gap-4">
        <p className="text-base font-semibold">Bring to the courthouse</p>
        <div className="flex flex-col gap-3">
          {[
            "The signed packet",
            `${decedentName}'s original will`,
            "A certified death certificate",
            "Filing fee",
          ].map((item) => (
            <div key={item} className="flex flex-row items-center gap-3">
              <Check className="size-4 text-brand shrink-0" aria-hidden="true" />
              <p className="text-sm text-secondary">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
